/**
 * Bring in a lab or association file: pick it, check how each row matched, settle the ones that
 * didn't, and file the rest. The original file is kept; coming back later only files what's left.
 */
import { useEffect, useState } from "react";
import { localDate } from "../../../contract";
import { RefPicker } from "../../../web/forms/pickers";
import { ApiError, api } from "../../../web/lib/api";
import { useFormat } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll } from "../../../web/lib/query";
import { useMe } from "../../../web/lib/session";
import { eventSummary } from "../../../web/shell/describe";
import { Button, Checkbox, Chip, Disclosure, ErrorNote, Field, TextArea, TextInput, toast } from "../../../web/ui/kit";
import type { DnaImportRow, ImportPreview } from "../shared/types";
import { readFileText } from "./ui";

type Kind = "dna" | "epd";

function StatusChip({ row }: { row: DnaImportRow }) {
  switch (row.status) {
    case "matched": return <Chip tone="good" icon="check">{row.animal?.by === "barcode" ? t("Matched by barcode") : row.animal?.by === "registration" ? t("Matched by registration") : row.animal?.by === "person" ? t("Picked by hand") : t("Matched by tag")}</Chip>;
    case "unmatched": return <Chip tone="warn" icon="warn">{t("No animal found")}</Chip>;
    case "ambiguous": return <Chip tone="warn" icon="warn">{t("More than one animal matches")}</Chip>;
    case "duplicate": return <Chip tone="neutral">{t("Already on file")}</Chip>;
    case "committed": return <Chip tone="good" icon="done">{t("Filed")}</Chip>;
    case "skipped": return <Chip tone="neutral">{t("Left out")}</Chip>;
  }
}

export function ImportPanel({ kind, onDone, resumeId }: { kind: Kind; onDone?: () => void; resumeId?: string }) {
  useLanguage();
  const { data: me } = useMe();
  const fmt = useFormat();
  const today = me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10);
  const defaultAssoc = (me?.ranch?.settings.genetics as { association?: string } | undefined)?.association ?? "";
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [lab, setLab] = useState("");
  const [association, setAssociation] = useState(defaultAssoc);
  const [date, setDate] = useState(today);
  const [release, setRelease] = useState("");
  const [preview, setPreview] = useState<(ImportPreview & { unknown?: string[] }) | null>(null);
  const [include, setInclude] = useState<Record<number, boolean>>({});
  const [picks, setPicks] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const base = kind === "dna" ? "/m/genetics/dna/import" : "/m/genetics/epds/import";

  const load = (p: ImportPreview) => {
    setPreview(p);
    setInclude(Object.fromEntries(p.rows.map((r) => [r.position, r.status === "matched"])));
    setPicks({});
  };
  useEffect(() => {
    if (!resumeId) return;
    let alive = true;
    setBusy(true);
    api.get<ImportPreview>(`${base}/${resumeId}`).then((p) => { if (alive) load(p); }).catch((err) => { if (alive) setError(err); }).finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, base]);

  const read = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = kind === "dna" ? { text, name: name || "results.csv", lab, report_date: date } : { text, name: name || "epds.csv", association, as_of: date, release: release || undefined };
      load(await api.post<ImportPreview>(`${base}/preview`, body));
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => include[r.position] && r.status !== "committed").map((r) => ({ position: r.position, ...(picks[r.position] ? { animal_id: picks[r.position] } : {}) }));
      const res = await api.post<{ committed: number; skipped: number }>(`${base}/${preview.file_id}/commit`, { rows });
      await invalidateAll();
      toast(plural(res.committed, "Filed {{count}} row", "Filed {{count}} rows"), { tone: "good" });
      load(await api.get<ImportPreview>(`${base}/${preview.file_id}`).catch(() => preview));
      onDone?.();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };

  if (!preview) {
    const ready = text.trim() && date && (kind === "dna" ? lab.trim() : association.trim());
    return (
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void read(); }}>
        <div className="genetics-drop">
          <Field label={kind === "dna" ? t("Results file from the lab") : t("EPD file from the association")} hint={t("A CSV file. Save a spreadsheet as CSV first.")}>
            <input type="file" accept=".csv,.txt,text/csv" aria-label={t("Choose a file")} onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setName(f.name); setText(await readFileText(f)); } }} />
          </Field>
          <Disclosure label={t("Paste it instead")}>
            <TextArea value={text} rows={6} onChange={(e) => setText(e.target.value)} aria-label={t("File text")} className="mono" />
          </Disclosure>
          {name && <p className="muted">{name}</p>}
        </div>
        {kind === "dna"
          ? <Field label={t("Lab")}><TextInput value={lab} onChange={(e) => setLab(e.target.value)} placeholder={"Neogen"} /></Field>
          : <Field label={t("Association or lab")}><TextInput value={association} onChange={(e) => setAssociation(e.target.value)} placeholder={"Red Angus (RAAA)"} /></Field>}
        <Field label={kind === "dna" ? t("Report date") : t("Evaluation run date")}><TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        {kind === "epd" && <Field label={t("Evaluation run")} optional><TextInput value={release} onChange={(e) => setRelease(e.target.value)} placeholder={t("Fall 2026")} /></Field>}
        {!!error && <ErrorNote error={error} />}
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!ready} icon="upload">{t("Check the file")}</Button></div>
      </form>
    );
  }

  const open = preview.rows.filter((r) => r.status !== "committed" && r.status !== "duplicate");
  const chosen = preview.rows.filter((r) => include[r.position] && r.status !== "committed" && (r.animal || picks[r.position]));
  const counts = {
    matched: preview.rows.filter((r) => r.status === "matched").length, problem: preview.rows.filter((r) => r.status === "unmatched" || r.status === "ambiguous").length,
    filed: preview.rows.filter((r) => r.status === "committed").length, dup: preview.rows.filter((r) => r.status === "duplicate").length,
  };
  return (
    <div className="stack">
      {preview.duplicate_file && <ErrorNote error={new Error(t("This file was brought in before. Rows already on file are marked."))} />}
      <div className="genetics-row-meta">
        <span>{plural(counts.matched, "{{count}} matched", "{{count}} matched")}</span>
        {counts.problem > 0 && <span>{plural(counts.problem, "{{count}} needs a look", "{{count}} need a look")}</span>}
        {counts.dup > 0 && <span>{plural(counts.dup, "{{count}} already on file", "{{count}} already on file")}</span>}
        {counts.filed > 0 && <span>{plural(counts.filed, "{{count}} filed", "{{count}} filed")}</span>}
      </div>
      {preview.unknown && preview.unknown.length > 0 && <p className="muted">{t("Columns kept but not read: {{columns}}", { columns: preview.unknown.join(", ") })}</p>}
      <ul className="genetics-rows">
        {preview.rows.map((r) => {
          const summary = kind === "dna" ? eventSummary({ type: "genetics.dna_result", data: { lab: lab || "", ...r.data }, subjects: [] }, fmt).replace(/^: /, "") : r.summary;
          const settled = r.status === "committed" || r.status === "duplicate";
          return (
            <li key={r.position} className="genetics-row">
              <div className="genetics-row-head">
                {!settled && <Checkbox checked={!!include[r.position]} onChange={(v) => setInclude({ ...include, [r.position]: v })} label={t("Include row {{n}}", { n: r.position })} />}
                <span className="genetics-row-title">
                  <span className="mono">{[r.barcode, r.tag, r.registration].filter(Boolean).join(" · ") || t("Row {{n}}", { n: r.position })}</span>
                  {r.animal && <span>→ {r.animal.label}</span>}
                </span>
                <StatusChip row={r} />
              </div>
              <span className="genetics-row-meta">{summary}</span>
              {(r.status === "unmatched" || r.status === "ambiguous" || (r.status === "matched" && include[r.position] && picks[r.position])) && (
                <div className="genetics-pick">
                  <RefPicker target="animal" value={picks[r.position] ?? null} onChange={(v) => { setPicks({ ...picks, [r.position]: (v as string) ?? "" }); if (v) setInclude({ ...include, [r.position]: true }); }} placeholder={r.candidates.length ? t("Pick which one") : t("Find the animal")} filter={{ status: "all" }} />
                </div>
              )}
              {r.status === "ambiguous" && r.candidates.length > 0 && <span className="muted">{t("Could be: {{animals}}", { animals: r.candidates.map((c) => c.label).join(", ") })}</span>}
            </li>
          );
        })}
      </ul>
      <div className="form-actions">
        <Button variant="primary" size="lg" icon="check" busy={busy} disabled={!chosen.length} onClick={() => void commit()}>{plural(chosen.length, "File {{count}} row", "File {{count}} rows")}</Button>
        {open.length === 0 && <span className="muted">{t("Everything in this file is settled.")}</span>}
      </div>
    </div>
  );
}
